import exceljs from 'exceljs'

import type { Cell } from './cell'
import type { Row } from './row'
import type { Sheet } from './sheet'

export class Workbook {
  public readonly sheets: Sheet[]

  constructor(...sheets: readonly Maybe.ReadonlyArray<Sheet>[]) {
    this.sheets = sheets.flat()
  }

  /**
   * Adds a {@link Sheet} to this {@link Workbook}
   */
  public addSheet(sheet: Sheet): this {
    this.sheets.push(sheet)
    return this
  }

  /**
   * Writes this {@link Workbook} out as `.xlsx` file bytes
   *
   * NOTE: typed as `Uint8Array` rather than Node's `Buffer` (which a real Node
   * `Buffer` still satisfies, since `Buffer extends Uint8Array`), because the
   * Node-specific `Buffer` type isn't reliably in scope everywhere this file
   * ends up getting compiled — not in a browser bundle (no `Buffer` global at
   * runtime), and not always in every backend typecheck path either, even
   * with the node types package installed (nx doesn't consistently thread a
   * project's own type config through every build that touches it).
   * `Uint8Array` sidesteps the whole question, being a plain ES2015+ type
   * rather than a Node-specific ambient one. The cast is type-only (erased at
   * compile time): exceljs ships its own thin ambient `Buffer` stub
   * (`interface Buffer extends ArrayBuffer`) that structurally conflicts with
   * the node types package's real, generic `Buffer<ArrayBufferLike>`, so
   * TypeScript sees an incompatible shape here even though there is only one
   * value at runtime
   */
  public async toBuffer(): Promise<Uint8Array> {
    const wb = this._toExceljsWorkbook()
    const data = await wb.xlsx.writeBuffer()
    return data as unknown as Uint8Array
  }

  /**
   * Converts this {@link Workbook} to an {@link exceljs.Workbook}
   */
  private _toExceljsWorkbook(): exceljs.Workbook {
    const wb = new exceljs.Workbook()

    // add data
    this.sheets.forEach(atnSheet => {
      const worksheet = wb.addWorksheet(atnSheet.name)
      const rows = atnSheet.rows.map(atnRow =>
        atnRow.cells.map(atnCell => atnCell.value),
      )
      worksheet.addRows(rows)

      const freezeRowCount = atnSheet.getFreezeRowCount()
      if (freezeRowCount) {
        worksheet.views = [{ state: 'frozen', ySplit: freezeRowCount }]
      }
    })

    // apply styles
    //
    // NOTE: this runs as a second pass over every sheet, after the "add data"
    // pass above has already bulk-written every row's raw values via
    // worksheet.addRows(). That bulk API is the fast path for values; styling
    // has no equivalent bulk form in exceljs, so it's applied cell-by-cell here
    // instead, via ws.getCell() (which returns the same cell instance addRows()
    // already created, rather than creating a new one)
    this.sheets.forEach(atnSheet => {
      const ws = wb.getWorksheet(atnSheet.name)
      if (ws === undefined) {
        throw new Error(`Worksheet ${atnSheet.name} not found`)
      }

      atnSheet.rows.forEach((atnRow, atnRowIdx) => {
        const row = ws.getRow(atnRowIdx + 1)
        atnRow.cells.forEach((atnCell, atnCellIdx) => {
          // exceljs is 1-indexed (row/column 0 doesn't exist), while our model
          // is 0-indexed like a normal array, hence the +1s throughout
          const cell = ws.getCell(atnRowIdx + 1, atnCellIdx + 1)
          // Copies over font/border/fill/alignment (see _applyStyle below)
          _applyStyle(cell, atnCell)

          if (atnCell.valueType) {
            // Overrides exceljs' own inferred type for this cell's value (e.g.
            // a numeric-looking string we want rendered as text, not a number)
            // with the type our model explicitly asked for
            cell.model.type = atnCell.valueType
          }

          if (atnCell.numberFormat) {
            // Excel format code controlling how a numeric/date value is
            // displayed (e.g. currency, percent, date) — see NumberFormat
            cell.numFmt = atnCell.numberFormat
          }

          // NOTE: merging N columns starting at this cell only works if every
          // following cell in `atnRow.cells` accounts for the columns this
          // merge consumes — see the columnSpan note on Cell for why
          if (atnCell.columnSpan) {
            const spanToCell = ws.getCell(
              atnRowIdx + 1,
              atnCellIdx + atnCell.columnSpan,
            )
            const mergeAddress = `${cell.address}:${spanToCell.address}`
            ws.mergeCells(mergeAddress)
          }
        })

        // Row-level style (e.g. Row.borderBottom()/borderTop(), which style
        // every cell in the row at once) is separate from each cell's own
        // style, and is applied once per row, after all of its cells
        _applyStyle(row, atnRow)
      })
      // apply column widths
      for (const { column, width } of atnSheet.getAllColumnWidths()) {
        ws.getColumn(column).width = width
      }
    })

    return wb
  }
}

/**
 * Copies styles from a {@link Cell} to an {@link exceljs.Cell}
 */
function _applyStyle(to: exceljs.Cell, from: Cell): void
/**
 * Copies styles from a {@link Row} to an {@link exceljs.Row}
 */
function _applyStyle(to: exceljs.Row, from: Row): void
function _applyStyle(to: exceljs.Cell | exceljs.Row, from: Cell | Row): void {
  if (to) {
    for (const styleKey in from.style) {
      const sourceStyle = from.style[styleKey as keyof typeof from.style]
      // NOTE: Since styleKey is the same on both sides, the property values
      // should align. However, TypeScript does not realize this. Since it
      // thinks we could be writing to any style property, it expects a value
      // that works for _every_ property. UnionToIntersection tells TypeScript
      // this value will work for every property, but really, we just know it
      // works for the current property, which is all that really matters.
      to[styleKey as keyof typeof from.style] =
        sourceStyle as UnionToIntersection<typeof sourceStyle>
    }
  }
}
